// #1168 RU carrier-snapshot edit modal.
//
// Edits `nameSnapshot` / `contactsSnapshot` on the `RussiaRequestCarrier`
// join row only — the parent `RussiaCarrier` directory entry is NEVER
// touched (separate edit surface in #1166). Empty form keeps submit
// disabled.
//
// @aiPurpose drives `PATCH /russia-requests/:id/carriers/:carrierId`
//   with `UpdateRussiaRequestCarrierBodySchema`.

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorCode, type RussiaRequestCarrier } from '@magimp/shared';

import { ApiClientError } from '../../../../api/errors.js';
import {
  Button,
  EditorModal,
  FormRow,
  Input,
  Textarea,
  useEditorGuard,
} from '../../../../ui/index.js';
import { useUpdateRussiaRequestCarrier } from '../../queries.js';

type EditErrorKey =
  | 'drawer.carriers.editCarrier.invalidStatus'
  | 'drawer.carriers.editCarrier.notFound'
  | 'drawer.carriers.editCarrier.generic';

function editErrorKey(err: unknown): EditErrorKey {
  if (err instanceof ApiClientError) {
    if (err.code === ErrorCode.INVALID_TRANSITION)
      return 'drawer.carriers.editCarrier.invalidStatus';
    if (err.code === ErrorCode.NOT_FOUND) return 'drawer.carriers.editCarrier.notFound';
  }
  return 'drawer.carriers.editCarrier.generic';
}

export interface RussiaEditCarrierModalProps {
  russiaRequestId: string;
  carrier: RussiaRequestCarrier;
  onClose: () => void;
}

export function RussiaEditCarrierModal({
  russiaRequestId,
  carrier,
  onClose,
}: RussiaEditCarrierModalProps) {
  const { t } = useTranslation(['russiaRequests', 'common']);
  const mutation = useUpdateRussiaRequestCarrier(russiaRequestId);

  const initialName = carrier.nameSnapshot ?? '';
  const initialContacts = carrier.contactsSnapshot ?? '';
  const [name, setName] = useState(initialName);
  const [contacts, setContacts] = useState(initialContacts);
  const baselineRef = useRef({ name: initialName, contacts: initialContacts });

  const nameTrimmed = name.trim();
  const contactsTrimmed = contacts.trim();
  const nameChanged = nameTrimmed !== baselineRef.current.name.trim();
  const contactsChanged = contactsTrimmed !== baselineRef.current.contacts.trim();
  const nameValid = nameTrimmed.length >= 1 && nameTrimmed.length <= 200;
  const contactsValid = contactsTrimmed.length >= 1 && contactsTrimmed.length <= 2000;
  // INVARIANT (#1168 B-2): server schema requires at least one of `name` /
  //   `contacts`. Submit must not enable for an empty diff or for fields
  //   that would fail length bounds on the server.
  const canSubmit =
    (nameChanged || contactsChanged) &&
    (!nameChanged || nameValid) &&
    (!contactsChanged || contactsValid) &&
    !mutation.isPending;

  async function onSubmit(): Promise<void> {
    if (!canSubmit) return;
    const input: { name?: string; contacts?: string } = {};
    if (nameChanged) input.name = nameTrimmed;
    if (contactsChanged) input.contacts = contactsTrimmed;
    try {
      await mutation.mutateAsync({ russiaRequestCarrierId: carrier.id, input });
      onClose();
    } catch {
      // surface read off `mutation.error`
    }
  }

  const dirty = nameChanged || contactsChanged;
  const testidSuffix = carrier.id;

  return (
    <EditorModal
      open
      dirty={dirty}
      busy={mutation.isPending}
      onConfirmClose={onClose}
      title={t('drawer.carriers.editCarrier.title', { code: carrier.displayCode })}
      width={420}
    >
      <div
        data-testid={`russia-request-drawer-carrier-edit-modal-${testidSuffix}`}
        style={{ display: 'grid', gap: 10 }}
      >
        <div data-testid={`russia-request-drawer-carrier-edit-name-${testidSuffix}`}>
          <FormRow label={t('drawer.carriers.add.nameLabel')}>
            <Input
              value={name}
              onChange={setName}
              ariaLabel={t('drawer.carriers.add.nameLabel')}
              invalid={nameChanged && !nameValid}
            />
          </FormRow>
        </div>
        <div data-testid={`russia-request-drawer-carrier-edit-contacts-${testidSuffix}`}>
          <FormRow label={t('drawer.carriers.add.contactsLabel')}>
            <Textarea
              value={contacts}
              onChange={setContacts}
              rows={3}
              ariaLabel={t('drawer.carriers.add.contactsLabel')}
            />
          </FormRow>
        </div>
        {mutation.isError && (
          <span
            role="alert"
            data-testid={`russia-request-drawer-carrier-edit-error-${testidSuffix}`}
            style={{ fontSize: 12, color: 'var(--status-error-fg)' }}
          >
            {t(editErrorKey(mutation.error))}
          </span>
        )}
        <EditCarrierFooter
          onClose={onClose}
          onSubmit={onSubmit}
          canSubmit={canSubmit}
          submitTestId={`russia-request-drawer-carrier-edit-submit-${testidSuffix}`}
        />
      </div>
    </EditorModal>
  );
}

function EditCarrierFooter({
  onClose,
  onSubmit,
  canSubmit,
  submitTestId,
}: {
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
  canSubmit: boolean;
  submitTestId: string;
}) {
  const { t } = useTranslation(['russiaRequests', 'common']);
  const { confirmDestructive } = useEditorGuard();
  return (
    <div
      style={{
        marginTop: 6,
        marginLeft: -16,
        marginRight: -16,
        marginBottom: -16,
        padding: '12px 16px',
        borderTop: '1px solid var(--border-2)',
        display: 'flex',
        gap: 8,
        justifyContent: 'flex-end',
      }}
    >
      <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
        {t('common:actions.cancel')}
      </Button>
      <Button
        kind="primary"
        disabled={!canSubmit}
        onClick={() => {
          void onSubmit();
        }}
        data-testid={submitTestId}
      >
        {t('drawer.carriers.editCarrier.submit')}
      </Button>
    </div>
  );
}
